import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import PartnerLeaderboard from "@/components/PartnerLeaderboard";
import PayoutRequest from "@/components/PayoutRequest";
import MomentsGallery from "@/components/MomentsGallery";
import HubHeader from "@/components/partner/HubHeader";
import EarningsFlow from "@/components/partner/EarningsFlow";
import ShareStudio from "@/components/partner/ShareStudio";
import NewHomePost from "@/components/partner/NewHomePost";
import PersonalInvite from "@/components/partner/PersonalInvite";
import GiftReset from "@/components/partner/GiftReset";
import FirstDay from "@/components/partner/FirstDay";
import Resources from "@/components/partner/Resources";
import Celebration from "@/components/partner/Celebration";
import { Section } from "@/components/partner/Section";
import ChannelStats from "@/components/partner/ChannelStats";
import FoundingWall from "@/components/partner/FoundingWall";
import AdelaidaWelcome from "@/components/AdelaidaWelcome";
import { AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const NAV = [["share", "Share"], ["invite", "Invite"], ["gift", "Gift"], ["launch", "First 24h"], ["moments", "Moments"], ["founders", "Founders"], ["resources", "Resources"], ["earnings", "Earnings"]];

function Referrals({ d }) {
  if (!d.referrals.length) return <p className="text-sm text-muted-foreground">No one yet. Your first share is one tap away.</p>;
  return (
    <div className="divide-y divide-border">
      {d.referrals.map((r, i) => (
        <div key={i} className="flex items-center justify-between py-3 text-sm" data-testid={`partner-ref-${i}`}>
          <span>{r.name}</span>
          <span className="text-muted-foreground text-xs">{r.joined} · <span className={r.status === "paid" ? "text-teal-400" : ""}>{r.status === "paid" ? "Member" : "Free"}</span></span>
        </div>
      ))}
    </div>
  );
}

export default function Partner() {
  const [k, setK] = useState(null);
  const [d, setD] = useState(null);
  const [welcome, setWelcome] = useState(false);
  const load = useCallback(() => Promise.all([api.get("/partner/kit"), api.get("/partner/me")]).then(([a, b]) => { setK(a.data); setD(b.data); }), []);
  useEffect(() => { load().catch(() => toast.error("Couldn't load your Partner Hub.")); }, [load]);
  useEffect(() => { if (k?.welcome_pending) setWelcome(true); }, [k?.welcome_pending]);
  const closeWelcome = () => { setWelcome(false); if (k.welcome_pending) { api.post("/partner/welcomed").catch(() => {}); setK((x) => ({ ...x, welcome_pending: false })); } };
  if (!k || !d) return <div className="min-h-screen bg-background"><Navbar /><div className="flex justify-center py-32"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div></div>;

  return (
    <div className="min-h-screen bg-background" data-testid="partner-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 pb-32">
        <HubHeader k={k} onWelcome={() => setWelcome(true)} />
        <EarningsFlow k={k} />
        <ChannelStats channels={k.channels} />
        <nav className="sticky top-16 z-20 -mx-5 px-5 py-3 mb-10 bg-background/80 backdrop-blur-xl border-y border-border flex gap-1 overflow-x-auto no-scrollbar" data-testid="partner-nav">
          {NAV.map(([id, label]) => (
            <button key={id} onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })} className="shrink-0 rounded-full px-4 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors" data-testid={`partner-nav-${id}`}>{label}</button>
          ))}
        </nav>
        <ShareStudio k={k} />
        <NewHomePost k={k} />
        <PersonalInvite k={k} onSent={load} />
        <GiftReset k={k} onSent={load} />
        <FirstDay steps={k.steps} />
        <Section id="moments" eyebrow="Coach moments" title="Lines worth sharing" sub="Standout lines from your conversations with the Coach. Each one shares as a beautiful card with your link.">
          <MomentsGallery />
        </Section>
        <FoundingWall />
        <Resources k={k} />
        <Section id="earnings" eyebrow="Earnings" title="What you've earned">
          <p className="text-xs text-muted-foreground mb-8" data-testid="partner-terms-note">{Math.round(k.rate * 100)}% of what each member pays in their first {k.months} months. Paid out so far: <span className="text-foreground" data-testid="partner-paid-out">£{d.paid_out_gbp.toFixed(2)}</span></p>
          <PayoutRequest d={d} onDone={load} />
          <PartnerLeaderboard />
          {d.payouts.length > 0 && (
            <div className="rounded-3xl border border-border bg-card/40 p-7 mb-8" data-testid="partner-payouts">
              <h3 className="font-serif text-2xl mb-4">Payouts</h3>
              <div className="divide-y divide-border">
                {d.payouts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-3 text-sm" data-testid={`partner-payout-${p.id}`}>
                    <span>{p.date}{p.note && <span className="text-muted-foreground"> · {p.note}</span>}</span>
                    <span className="text-teal-400">£{p.amount_gbp.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-3xl border border-border bg-card/40 p-7" data-testid="partner-referrals">
            <h3 className="font-serif text-2xl mb-4">People you've brought</h3>
            <Referrals d={d} />
          </div>
        </Section>
      </main>
      {!welcome && <Celebration keys={k.celebrate} />}
      <AnimatePresence>{welcome && <AdelaidaWelcome key="pw" src="/partner/welcome" eyebrow="Welcome, Partner" onClose={closeWelcome} />}</AnimatePresence>
    </div>
  );
}
